import React, { useState, useEffect } from 'react';
import type { ClassifyScreen as ClassifyScreenType } from '../../types/course';
import { playTickSound, playCorrectSound, playIncorrectSound } from '../../utils/sound';

interface Props {
  screen: ClassifyScreenType;
  onInteractionComplete: () => void;
  completed: boolean;
}

export const ClassifyScreen: React.FC<Props> = ({
  screen,
  onInteractionComplete,
  completed,
}) => {
  const [currentIndex, setCurrentIndex] = useState(completed ? screen.items.length : 0);
  const [results, setResults] = useState<('correct' | 'wrong')[]>(
    completed ? screen.items.map(() => 'correct') : []
  );
  const [feedback, setFeedback] = useState<'correct' | 'wrong' | null>(null);

  const allDone = currentIndex >= screen.items.length;
  const currentItem = !allDone ? screen.items[currentIndex] : null;

  useEffect(() => {
    if (allDone && !completed) {
      onInteractionComplete();
      playCorrectSound();
    }
  }, [allDone, completed, onInteractionComplete]);

  const handleClassify = (bucket: 'A' | 'B') => {
    if (!currentItem || feedback) return;

    const isCorrect = bucket === currentItem.bucket;
    if (isCorrect) {
      playTickSound();
      setFeedback('correct');
    } else {
      playIncorrectSound();
      setFeedback('wrong');
    }

    setResults((prev) => [...prev, isCorrect ? 'correct' : 'wrong']);

    setTimeout(() => {
      setFeedback(null);
      setCurrentIndex((prev) => prev + 1);
    }, 500);
  };

  return (
    <div className="flex flex-col gap-4 px-1">
      {screen.icon && <div className="text-3xl text-center">{screen.icon}</div>}
      <h3 className="text-lg font-semibold font-serif text-[var(--clue-text-primary)] text-center">
        {screen.title}
      </h3>

      {screen.body && (
        <>
          <p className="text-sm leading-relaxed text-[var(--clue-text-secondary)]">
            {screen.body}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <div className="flex-1 h-px bg-[var(--clue-border-mid)]" />
            <span className="text-[10px] font-semibold text-[var(--clue-text-tertiary)] uppercase tracking-widest">Your turn</span>
            <div className="flex-1 h-px bg-[var(--clue-border-mid)]" />
          </div>
        </>
      )}

      {/* Progress */}
      <div className="flex gap-1 justify-center">
        {screen.items.map((_, i) => (
          <div
            key={i}
            className={`w-2 h-2 rounded-full transition-colors ${
              i < results.length
                ? results[i] === 'correct'
                  ? 'bg-[var(--clue-green-accent)]'
                  : 'bg-red-400'
                : i === currentIndex
                  ? 'bg-[var(--clue-text-secondary)]'
                  : 'bg-[var(--clue-border-mid)]'
            }`}
          />
        ))}
      </div>

      {currentItem ? (
        <>
          {/* Current item card */}
          <div className={`rounded-lg border p-4 text-center transition-all duration-200 ${
            feedback === 'correct'
              ? 'bg-[var(--clue-green-accent)]/10 border-[var(--clue-green-accent)]/30'
              : feedback === 'wrong'
                ? 'bg-red-500/10 border-red-400/30'
                : 'bg-[var(--clue-bg-secondary)] border-[var(--clue-border-mid)]'
          }`}>
            <p className="text-sm font-medium text-[var(--clue-text-primary)]">
              {currentItem.text}
            </p>
          </div>

          {/* Bucket buttons */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => handleClassify('A')}
              disabled={feedback !== null}
              className="py-3 rounded-lg border-2 border-[var(--clue-green-accent)]/50 bg-[var(--clue-green-accent)]/5 text-sm font-semibold text-[var(--clue-green-accent)] hover:bg-[var(--clue-green-accent)]/15 transition-colors"
            >
              {screen.bucketA}
            </button>
            <button
              onClick={() => handleClassify('B')}
              disabled={feedback !== null}
              className="py-3 rounded-lg border-2 border-[var(--clue-teal-accent)]/50 bg-[var(--clue-teal-accent)]/5 text-sm font-semibold text-[var(--clue-teal-accent)] hover:bg-[var(--clue-teal-accent)]/15 transition-colors"
            >
              {screen.bucketB}
            </button>
          </div>
        </>
      ) : (
        /* Summary */
        <div className="rounded-lg bg-[var(--clue-green-accent)]/10 border border-[var(--clue-green-accent)]/30 p-4 text-center">
          <p className="text-sm font-medium text-[var(--clue-green-accent)]">
            All sorted! {results.filter((r) => r === 'correct').length}/{results.length} correct
          </p>
        </div>
      )}
    </div>
  );
};
